import React, { useEffect, useMemo, useState } from "react";
import emailjs from "@emailjs/browser";
import Header from "../../components/Header";
import { logAnalyticsEvent } from "../../Firebase";
import "./Public.css";
import Footer from "../../components/Footer";
import useSeo from "../../hooks/useSeo";

const pubUrl = process.env.PUBLIC_URL || "";

// Basic helpers
const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());
const onlyDigits = (v) => String(v || "").replace(/[^\d]/g, "");
const isCanadianPostalCode = (v) => /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(String(v || "").trim());

export default function Contact() {
    useSeo({
  title: "Contact Crowned Roofing | Toronto, ON",
  description:
    "Request an inspection, repair evaluation, or proposal. Contact Crowned Roofing for roofing services in Toronto and surrounding Ontario communities.",
  ogImage: "https://www.crownedroofing.com/images/ogimg.png",
  canonical: "https://www.crownedroofing.com/contact",
});


  useEffect(() => {
    document.title = "Contact | Crowned Roofing";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Contact Crowned Roofing for commercial roofing services, inspections, repairs, and project planning."
      );
    }
  }, []);

  const CONTACT = useMemo(
    () => ({
      phoneDisplay: "(437) 435 3185",
      phoneDial: "+14374353185",
      email: "info@crownedroofing.com",
      // optional:
      addressLine1: "Toronto, ON  ",
      addressLine2: "",
      hours: "Monday–Saturday, 8:00AM - 7:00PM",
    }),
    []
  );

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    zip: "",
    message: "",
    interests: {
      commercialRoofing: true,
      roofInspections: true,
      repairs: false,
      maintenance: false,
      replacement: false,
    },
  });

  const [status, setStatus] = useState({ state: "idle", msg: "" }); // idle | sending | success | error
  const [touched, setTouched] = useState({});

  const update = (key, value) => setForm((p) => ({ ...p, [key]: value }));
  const updateInterest = (key, value) =>
    setForm((p) => ({ ...p, interests: { ...p.interests, [key]: value } }));

  const errors = useMemo(() => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.email.trim() || !isEmail(form.email)) e.email = "Enter a valid email";
    if (!onlyDigits(form.phone).length) e.phone = "Required";
    if (form.zip.trim() && !isCanadianPostalCode(form.zip)) e.zip = "Enter a valid Postal Code (e.g., A0B1C2)";
    if (!form.message.trim()) e.message = "Required";
    return e;
  }, [form]);

  const hasErrors = Object.keys(errors).length > 0;

  const markTouched = (key) => setTouched((p) => ({ ...p, [key]: true }));

  const serializeInterests = (obj) =>
    Object.entries(obj)
      .filter(([, v]) => v)
      .map(([k]) => k)
      .join(", ");

  const onSubmit = async (e) => {
    e.preventDefault();

    setTouched({
      name: true,
      email: true,
      phone: true,
      zip: true,
      message: true,
    });

    if (hasErrors) {
      logAnalyticsEvent("contact_submit_error", {
        reason: "validation",
        errors: Object.keys(errors).join(","),
      });
      setStatus({ state: "error", msg: "Please fix the highlighted fields and try again." });
      return;
    }

    setStatus({ state: "sending", msg: "" });
    logAnalyticsEvent("contact_submit_attempt", {
      interests: serializeInterests(form.interests),
    });

    try {
      const serviceId = "service_8rf8hvg"; // replace with your EmailJS service ID
      const templateId = "template_69na60h"; // replace with your EmailJS contact template ID
      const publicKey = "M0qjSu_kVW2st71pt"; // replace with your EmailJS public key

      if (!serviceId || !templateId || !publicKey) {
        setStatus({
          state: "error",
          msg: "EmailJS is not configured. Please set your EmailJS IDs directly in Contact.js.",
        });
        return;
      }

      const templateParams = {
        contact_name: form.name.trim(),
        contact_email: form.email.trim(),
        contact_phone: form.phone.trim(),
        contact_postal_code: form.zip.trim(),
        contact_interests: serializeInterests(form.interests),
        contact_message: form.message.trim(),
      };

      await emailjs.send(serviceId, templateId, templateParams, { publicKey });

      logAnalyticsEvent("contact_submit_success", {
        interests: serializeInterests(form.interests),
      });

      setStatus({ state: "success", msg: "Message sent. We’ll get back to you shortly." });

      setForm({
        name: "",
        email: "",
        phone: "",
        zip: "",
        message: "",
        interests: {
          commercialRoofing: false,
          roofInspections: false,
          repairs: false,
          maintenance: false,
          replacement: false,
        },
      });
      setTouched({});
    } catch (err) {
      logAnalyticsEvent("contact_submit_error", { reason: "send_failed" });
      setStatus({
        state: "error",
        msg: err?.text || err?.message || "Something went wrong sending your message. Please try again or call us.",
      });
    }
  };

  return (
    <div className="contactPage">
      <Header />

      {/* HERO */}
      <section className="contactHero">
        <div
          className="contactHeroBg"
          aria-hidden="true"
          style={{ backgroundImage: `url(${pubUrl}/images/contact/contact-hero.jpg)` }}
        />
        <div className="contactHeroShade" aria-hidden="true" />

        <div className="contactHeroInner">
          <h1>Contact Crowned Roofing</h1>
          <p>
            Reach out for commercial roofing services, inspections, repairs, and project planning. Clear communication,
            documented scope, and dependable execution.
          </p>
        </div>
      </section>

      {/* BODY */}
      <section className="contactBody">
        <div className="contactContainer contactGrid">
          {/* FORM */}
          <div className="contactCard">
            <div className="contactCardTitle">Contact Form</div>
            <div className="contactRule" />

            <form onSubmit={onSubmit} className="contactForm" noValidate>
              <div className="fieldRow two">
                <div className="field">
                  <label>
                    Full Name <span className="req">*</span>
                  </label>
                  <input
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    onBlur={() => markTouched("name")}
                    placeholder="Your name"
                    className={touched.name && errors.name ? "bad" : ""}
                    autoComplete="name"
                  />
                  {touched.name && errors.name ? <div className="err">{errors.name}</div> : null}
                </div>

                <div className="field">
                  <label>
                    E-Mail Address <span className="req">*</span>
                  </label>
                  <input
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    onBlur={() => markTouched("email")}
                    placeholder="you@company.com"
                    className={touched.email && errors.email ? "bad" : ""}
                    autoComplete="email"
                  />
                  {touched.email && errors.email ? <div className="err">{errors.email}</div> : null}
                </div>
              </div>

              <div className="fieldRow two">
                <div className="field">
                  <label>
                    Phone Number <span className="req">*</span>
                  </label>
                  <input
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    onBlur={() => markTouched("phone")}
                    placeholder="(555) 555-5555"
                    className={touched.phone && errors.phone ? "bad" : ""}
                    autoComplete="tel"
                  />
                  {touched.phone && errors.phone ? <div className="err">{errors.phone}</div> : null}
                </div>

                <div className="field">
                  <label>Project Postal Code</label>
                  <input
                    value={form.zip}
                    onChange={(e) => update("zip", e.target.value.toUpperCase())}
                    onBlur={() => markTouched("zip")}
                    placeholder="A0B 1C2"
                    className={touched.zip && errors.zip ? "bad" : ""}
                    autoComplete="postal-code"
                  />
                  {touched.zip && errors.zip ? <div className="err">{errors.zip}</div> : null}
                </div>
              </div>

              <div className="field">
                <label>
                  Message <span className="req">*</span>
                </label>
                <textarea
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  onBlur={() => markTouched("message")}
                  placeholder="Tell us what you need help with (roof type, timeline, known issues, etc.)"
                  className={touched.message && errors.message ? "bad" : ""}
                  rows={7}
                />
                {touched.message && errors.message ? <div className="err">{errors.message}</div> : null}
              </div>

              <div className="interests">
                <div className="interestsLabel">
                  I am interested in <span className="req">*</span>
                </div>

                <label className="check">
                  <input
                    type="checkbox"
                    checked={form.interests.commercialRoofing}
                    onChange={(e) => updateInterest("commercialRoofing", e.target.checked)}
                  />
                  <span>Commercial Roofing</span>
                </label>

                <label className="check">
                  <input
                    type="checkbox"
                    checked={form.interests.roofInspections}
                    onChange={(e) => updateInterest("roofInspections", e.target.checked)}
                  />
                  <span>Roof Inspections</span>
                </label>

                <label className="check">
                  <input
                    type="checkbox"
                    checked={form.interests.repairs}
                    onChange={(e) => updateInterest("repairs", e.target.checked)}
                  />
                  <span>Repairs</span>
                </label>

                <label className="check">
                  <input
                    type="checkbox"
                    checked={form.interests.maintenance}
                    onChange={(e) => updateInterest("maintenance", e.target.checked)}
                  />
                  <span>Maintenance Planning</span>
                </label>

                <label className="check">
                  <input
                    type="checkbox"
                    checked={form.interests.replacement}
                    onChange={(e) => updateInterest("replacement", e.target.checked)}
                  />
                  <span>Replacement Planning</span>
                </label>
              </div>

              {status.state !== "idle" ? (
                <div className={`status ${status.state}`}>{status.msg}</div>
              ) : null}

              <button className="contactBtn" type="submit" disabled={status.state === "sending"}>
                {status.state === "sending" ? "SENDING…" : "SEND MESSAGE"}
              </button>

              <div className="finePrint">
                Prefer email? Send photos and documentation to{" "}
                <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>.
              </div>
            </form>
          </div>

          {/* KEY INFO */}
          <aside className="contactCard infoCard">
            <div className="contactCardTitle">Key Information</div>
            <div className="contactRule" />

            <div className="infoList">
              <div className="infoItem">
                <div className="infoIcon" aria-hidden="true">☎</div>
                <div className="infoBody">
                  <a
                    className="infoMain"
                    href={`tel:${CONTACT.phoneDial}`}
                    onClick={() => logAnalyticsEvent("contact_phone_click", { source: "contact_page" })}
                  >
                    {CONTACT.phoneDisplay}
                  </a>
                  <div className="infoSub">{CONTACT.hours}</div>
                </div>
              </div>

              <div className="infoItem">
                <div className="infoIcon" aria-hidden="true">✉</div>
                <div className="infoBody">
                  <a
                    className="infoMain"
                    href={`mailto:${CONTACT.email}`}
                    onClick={() => logAnalyticsEvent("contact_email_click", { source: "contact_page" })}
                  >
                    {CONTACT.email}
                  </a>
                  <div className="infoSub">For inquiries, photos, and documentation.</div>
                </div>
              </div>

              <div className="infoItem">
                <div className="infoIcon" aria-hidden="true">⌁</div>
                <div className="infoBody">
                  <div className="infoMain">Crowned Roofing</div>
                  <div className="infoSub">
                    {CONTACT.addressLine1}
                    {CONTACT.addressLine2 ? <><br />{CONTACT.addressLine2}</> : null}
                  </div>
                </div>
              </div>
            </div>

            <button
              className="contactBtn ghost"
              onClick={() => {
                logAnalyticsEvent("contact_secondary_cta_click", { source: "contact_page" });
                window.location.href = `tel:${CONTACT.phoneDial}`;
              }}
            >
              CALL NOW
            </button>

            <div className="infoNote">
              If you have an existing report, photos, or maintenance history, include it in your message and we’ll review
              before recommending next steps.
            </div>
          </aside>
        </div>
      </section>
      <Footer/>
    </div>
  );
}
